import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, UserX, UserCheck, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_LABELS = { open: "Ouvert", in_progress: "En cours", resolved: "Résolu", closed: "Fermé" };
const STATUS_STYLES = {
  open: "bg-red-50 text-red-700 border-red-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-stone-100 text-stone-500 border-stone-200",
};
const PRIORITY_LABELS = { low: "Faible", normal: "Normal", high: "Haute", urgent: "Urgent" };

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SuperSupportTicket() {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [unblockConfirm, setUnblockConfirm] = useState(false);
  const [unblocking, setUnblocking] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/superadmin/support/tickets/${ticketId}`)
      .then(({ data }) => {
        setTicket(data);
        setAdminNotes(data.admin_notes || "");
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/superadmin/support/tickets/${ticketId}`, { status });
      setTicket(data);
      toast.success(`Statut mis à jour : ${STATUS_LABELS[status]}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changePriority = async (priority) => {
    try {
      const { data } = await api.patch(`/superadmin/support/tickets/${ticketId}`, { priority });
      setTicket(data);
      toast.success("Priorité mise à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/superadmin/support/tickets/${ticketId}`, { admin_notes: adminNotes });
      setTicket(data);
      toast.success("Notes enregistrées");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNotes(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/superadmin/support/tickets/${ticketId}/messages`, { message: newMsg.trim() });
      setTicket(data);
      setNewMsg("");
      toast.success("Réponse envoyée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const unblockUser = async () => {
    setUnblocking(true);
    try {
      await api.patch(`/superadmin/users/${ticket.user_id}/unblock`);
      toast.success("Compte débloqué");
      // Fermer le ticket automatiquement après déblocage
      await api.patch(`/superadmin/support/tickets/${ticketId}`, { status: "resolved" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUnblocking(false);
      setUnblockConfirm(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-ash text-sm">Chargement…</div>;
  if (!ticket) return <div className="text-center py-16 text-ash text-sm">Ticket introuvable</div>;

  const isClosed = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div className="space-y-6 max-w-3xl">
      {/* En-tête */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/admin/support")}
          className="text-ash hover:text-ink transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="font-serif text-2xl text-ink truncate">
            Ticket #{ticket.id.slice(-8).toUpperCase()}
          </h1>
          <p className="text-ash text-sm">{ticket.subject} · Créé le {fmt(ticket.created_at)}</p>
        </div>
        <button onClick={load} className="text-ash hover:text-ink transition-colors">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-4">
          {/* Message initial du client */}
          <div className="bg-white border border-stone rounded-md p-4">
            <p className="text-xs uppercase tracking-widest text-ash mb-2">Message du client</p>
            <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{ticket.message}</p>
            <p className="text-xs text-ash mt-3">{fmt(ticket.created_at)}</p>
          </div>

          {/* Messages de suivi */}
          {ticket.messages && ticket.messages.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-ash">Échanges</p>
              {ticket.messages.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-md p-4 border ${
                    m.author_type === "admin"
                      ? "bg-ink/5 border-stone ml-4"
                      : "bg-white border-stone mr-4"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-ink">
                      {m.author_type === "admin" ? `${m.author_name} (équipe)` : "Client"}
                    </p>
                    <p className="text-xs text-ash">{fmt(m.created_at)}</p>
                  </div>
                  <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{m.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* Formulaire de réponse */}
          {!isClosed && (
            <form onSubmit={sendMessage} className="bg-white border border-stone rounded-md p-4 space-y-3">
              <p className="text-xs uppercase tracking-widest text-ash">Ajouter une réponse</p>
              <textarea
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                rows={4}
                placeholder="Votre réponse…"
                className="w-full border border-stone rounded px-3 py-2 text-sm text-ink bg-white resize-none focus:outline-none focus:border-copper transition-colors"
              />
              <button
                type="submit"
                disabled={sending || !newMsg.trim()}
                className="flex items-center gap-2 bg-ink text-white px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-graphite transition-colors disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                {sending ? "Envoi…" : "Envoyer"}
              </button>
            </form>
          )}
        </div>

        {/* Sidebar droite */}
        <div className="space-y-4">
          {/* Infos client */}
          <div className="bg-white border border-stone rounded-md p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-ash">Client</p>
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-ink">{ticket.user_name || "—"}</p>
              <p className="text-xs text-ash break-all">{ticket.user_email}</p>
              <p className="text-xs text-ash">{ticket.user_phone || "Téléphone non renseigné"}</p>
            </div>

            {/* Bouton débloquer */}
            {ticket.category === "account_blocked" && !isClosed && (
              <button
                onClick={() => setUnblockConfirm(true)}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white py-2 text-xs uppercase tracking-[0.12em] hover:bg-emerald-700 transition-colors rounded"
              >
                <UserCheck className="h-3.5 w-3.5" />
                Débloquer le compte
              </button>
            )}
          </div>

          {/* Statut & Priorité */}
          <div className="bg-white border border-stone rounded-md p-4 space-y-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest text-ash">Statut</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs border rounded px-2 py-1 ${STATUS_STYLES[ticket.status] || ""}`}>
                  {STATUS_LABELS[ticket.status] || ticket.status}
                </span>
              </div>
              {!isClosed && (
                <Select value={ticket.status} onValueChange={changeStatus}>
                  <SelectTrigger className="text-xs h-8">
                    <SelectValue placeholder="Changer le statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Ouvert</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="resolved">Résolu</SelectItem>
                    <SelectItem value="closed">Fermé</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest text-ash">Priorité</p>
              <Select value={ticket.priority || "normal"} onValueChange={changePriority} disabled={isClosed}>
                <SelectTrigger className="text-xs h-8">
                  <SelectValue placeholder="Priorité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Faible</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {ticket.closed_at && (
              <div>
                <p className="text-xs uppercase tracking-widest text-ash mb-1">Fermé le</p>
                <p className="text-xs text-ink">{fmt(ticket.closed_at)}</p>
                {ticket.closed_by && <p className="text-xs text-ash">par {ticket.closed_by}</p>}
              </div>
            )}
          </div>

          {/* Notes internes */}
          <div className="bg-white border border-stone rounded-md p-4 space-y-2">
            <p className="text-xs uppercase tracking-widest text-ash">Notes internes</p>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              placeholder="Notes visibles uniquement par l'équipe…"
              className="w-full border border-stone rounded px-3 py-2 text-xs text-ink bg-white resize-none focus:outline-none focus:border-copper transition-colors"
            />
            <button
              onClick={saveNotes}
              disabled={savingNotes}
              className="text-xs text-copper hover:text-copper/70 transition-colors disabled:opacity-50"
            >
              {savingNotes ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation déblocage */}
      <AlertDialog open={unblockConfirm} onOpenChange={setUnblockConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Débloquer ce compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le client <strong>{ticket.user_email}</strong> pourra à nouveau se connecter normalement.
              Le ticket passera automatiquement en statut « Résolu ».
              Un email de notification sera envoyé au client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={unblockUser}
              disabled={unblocking}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {unblocking ? "Déblocage…" : "Débloquer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
